import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  Monitor, 
  ArrowRight,
  Database,
  Truck,
  Users,
  BrainCircuit,
  Search,
  Maximize2,
  FileSpreadsheet,
  ClipboardList,
  Activity,
  DollarSign,
  Smartphone,
  ShieldAlert,
  BarChart3,
  Calendar,
  FileText,
  MapPin,
  PieChart,
  HeartPulse
} from 'lucide-react';

interface GalleryItem {
  id: string;
  category: 'sales_finance' | 'logistics' | 'manufacturing' | 'hr_ai';
  tabTitle: { es: string; en: string };
  title: { es: string; en: string };
  subtitle: { es: string; en: string };
  imageSrc: string;
  badge: { es: string; en: string };
  highlights: {
    title: { es: string; en: string };
    desc: { es: string; en: string };
  }[];
}

const categories = [
  { id: 'sales_finance', label: { es: 'Ventas y Finanzas', en: 'Sales & Finance' }, icon: <DollarSign className="w-4 h-4" /> },
  { id: 'logistics', label: { es: 'Logística (TMS)', en: 'Logistics (TMS)' }, icon: <Truck className="w-4 h-4" /> },
  { id: 'manufacturing', label: { es: 'Manufactura (MRP)', en: 'Manufacturing (MRP)' }, icon: <Activity className="w-4 h-4" /> },
  { id: 'hr_ai', label: { es: 'Talento e IA', en: 'HR & AI Intelligence' }, icon: <BrainCircuit className="w-4 h-4" /> }
];

const galleryData: GalleryItem[] = [
  // ==================== CATEGORY: SALES & FINANCE ====================
  {
    id: 'crm',
    category: 'sales_finance',
    tabTitle: { es: 'CRM Comercial', en: 'CRM Sales' },
    title: { es: 'Gestión Comercial y CRM', en: 'Sales & CRM Management' },
    subtitle: { es: 'Administra tus prospectos, embudos de venta e historial de interacciones en una sola vista limpia.', en: 'Manage your leads, sales funnels, and interaction history in a single clean view.' },
    imageSrc: '/crm_dashboard.png',
    badge: { es: 'Clientes y Ventas', en: 'Sales & Pipeline' },
    highlights: [
      {
        title: { es: 'Embudo de Ventas Dinámico', en: 'Dynamic Sales Funnel' },
        desc: { es: 'Mueve tratos entre etapas visuales intuitivas (Prospecto, Negociación, Cerrado) con total facilidad.', en: 'Move deals across intuitive visual stages (Lead, Negotiation, Closed) with complete ease.' }
      },
      {
        title: { es: 'Historial Unificado', en: 'Unified Timeline' },
        desc: { es: 'Registro cronológico completo de correos, llamadas, cotizaciones y notas del cliente.', en: 'Complete chronological history of emails, calls, quotes, and customer notes.' }
      },
      {
        title: { es: 'Métricas en Tiempo Real', en: 'Real-time Metrics' },
        desc: { es: 'Pronósticos de venta automáticos y medición del desempeño del equipo comercial.', en: 'Automatic sales forecasting and performance metrics for the sales team.' }
      }
    ]
  },
  {
    id: 'crm_calendar',
    category: 'sales_finance',
    tabTitle: { es: 'Agenda y Citas', en: 'Calendar & Meetings' },
    title: { es: 'Agenda de Actividades y Citas de Clientes', en: 'Activity & Client Appointments Calendar' },
    subtitle: { es: 'Control de citas comerciales, visitas programadas y tareas pendientes vinculadas a prospectos del CRM.', en: 'Control of commercial appointments, scheduled visits, and pending tasks linked to CRM leads.' },
    imageSrc: '/crm_calendar.png',
    badge: { es: 'Agenda Comercial', en: 'Sales Calendar' },
    highlights: [
      {
        title: { es: 'Vista de Día, Semana y Mes', en: 'Day, Week & Month Views' },
        desc: { es: 'Navega rápidamente entre diferentes granularidades temporales para coordinar tu agenda.', en: 'Quickly navigate between different time granularities to coordinate your agenda.' }
      },
      {
        title: { es: 'Vinculación Directa al CRM', en: 'Direct CRM Linking' },
        desc: { es: 'Crea minutas de reunión y asigna tareas de seguimiento directamente desde el evento.', en: 'Create meeting minutes and assign follow-up tasks directly from the event.' }
      },
      {
        title: { es: 'Sincronización Multidispositivo', en: 'Multi-device Synchronization' },
        desc: { es: 'Agenda sincronizada con calendarios externos para alertar a los vendedores en ruta.', en: 'Calendar synced with external providers to alert sales agents on the road.' }
      }
    ]
  },
  {
    id: 'erp',
    category: 'sales_finance',
    tabTitle: { es: 'ERP SAT CFDI', en: 'ERP SAT CFDI' },
    title: { es: 'Buscador de Comprobantes SAT CFDI', en: 'SAT CFDI Invoicing Explorer' },
    subtitle: { es: 'Validación, descarga masiva y auditoría inteligente de facturas fiscales 4.0 directamente sincronizado con el SAT.', en: 'Validation, bulk download, and smart audit of tax invoices 4.0 directly synced with Mexican SAT.' },
    imageSrc: '/erp_contaelite.png',
    badge: { es: 'SAT y Contabilidad', en: 'Tax & Compliance' },
    highlights: [
      {
        title: { es: 'Descarga Masiva XML', en: 'Bulk XML Downloader' },
        desc: { es: 'Sincronización instantánea con el portal del SAT para descargar y resguardar facturas emitidas y recibidas.', en: 'Instant sync with the SAT portal to download and safeguard issued and received invoices.' }
      },
      {
        title: { es: 'Validación Fiscal SAT', en: 'Fiscal SAT Validation' },
        desc: { es: 'Verifica en tiempo real el estatus del CFDI (Vigente/Cancelado) y detecta EFOS de forma automática.', en: 'Check real-time CFDI status (Active/Cancelled) and automatically detect EFOS blacklist hits.' }
      },
      {
        title: { es: 'Filtros y Contabilidad', en: 'Search Filters & Bookkeeping' },
        desc: { es: 'Filtra por RFC, emisor, receptor, fecha y folio fiscal con búsquedas de alto rendimiento.', en: 'Filter by RFC, issuer, recipient, date, and UUID with high-performance search queries.' }
      }
    ]
  },
  {
    id: 'erp_xml',
    category: 'sales_finance',
    tabTitle: { es: 'Detalle XML SAT', en: 'XML Invoice Detail' },
    title: { es: 'Consulta y Resguardo de Facturas SAT', en: 'SAT XML Invoice Explorer' },
    subtitle: { es: 'Desglose detallado de emisores, receptores, conceptos facturados y folios fiscales en el ERP.', en: 'Detailed breakdown of issuers, recipients, invoiced line items, and UUIDs within the ERP.' },
    imageSrc: '/erp_xml_search.png',
    badge: { es: 'Facturación SAT', en: 'Tax Invoicing' },
    highlights: [
      {
        title: { es: 'Estatus del Contribuyente', en: 'Taxpayer Status Check' },
        desc: { es: 'Validación automatizada de la situación fiscal del cliente o proveedor (Activo/Inactivo).', en: 'Automated validation of client or supplier tax status (Active/Inactive).' }
      },
      {
        title: { es: 'Descarga Masiva Directa', en: 'Direct Bulk Download' },
        desc: { es: 'Botón directo de exportación y descarga de archivos XML y representaciones impresas PDF.', en: 'Direct button to export and download XML files and printed PDF representations.' }
      },
      {
        title: { es: 'Asociación Contable', en: 'Bookkeeping Association' },
        desc: { es: 'Vincula de forma automática la factura fiscal con las pólizas de egresos o ingresos.', en: 'Automatically links the tax invoice with corresponding expense or income vouchers.' }
      }
    ]
  },
  {
    id: 'kpis_gastos',
    category: 'sales_finance',
    tabTitle: { es: 'KPIs de Gastos', en: 'Expense KPIs' },
    title: { es: 'Dashboard de Indicadores y Gastos Generales', en: 'Expenses & Sales KPIs Dashboard' },
    subtitle: { es: 'Control de KPIs estratégicos de ventas, presupuestos de compras y control de gastos generales por área.', en: 'Control of strategic sales KPIs, purchasing budgets, and general expense tracking per business unit.' },
    imageSrc: '/kpi_expenses.png',
    badge: { es: 'Inteligencia Empresarial', en: 'Business Intelligence' },
    highlights: [
      {
        title: { es: 'Resumen de Movimientos', en: 'Transaction Summaries' },
        desc: { es: 'Visualización rápida de presupuestos asignados contra gastos reales erogados por departamento.', en: 'Quick visualization of assigned budgets versus actual expenditures per department.' }
      },
      {
        title: { es: 'KPIs por Vendedor', en: 'Salesperson Metrics' },
        desc: { es: 'Evaluación de desempeño, cumplimiento de metas de ventas y comisiones generadas por agente comercial.', en: 'Performance evaluation, sales targets achievement, and commissions generated per sales representative.' }
      },
      {
        title: { es: 'Gastos Operativos (OPEX)', en: 'Operating Expenses (OPEX)' },
        desc: { es: 'Monitoreo dinámico del gasto operativo de sucursales con desglose de cuentas contables.', en: 'Dynamic monitoring of operational expenditure across branches with account ledger breakdowns.' }
      }
    ]
  },
  // ==================== CATEGORY: LOGISTICS ====================
  {
    id: 'tms',
    category: 'logistics',
    tabTitle: { es: 'TMS Embarques', en: 'TMS Shipments' },
    title: { es: 'Logística y Monitoreo de Embarques (Munters)', en: 'Logistics & Shipments Tracking (Munters)' },
    subtitle: { es: 'Gestión de fletes, asignación de unidades de transporte y seguimiento en tiempo real con Carta Porte.', en: 'Freight management, transport unit assignment, and real-time tracking with Carta Porte.' },
    imageSrc: '/tms_munters.png',
    badge: { es: 'Transporte y Distribución', en: 'Fleet & Logistics' },
    highlights: [
      {
        title: { es: 'Seguimiento de Unidades', en: 'Fleet Location Tracking' },
        desc: { es: 'Monitorea el estatus de tus viajes, ubicaciones de transportistas y tiempos de entrega.', en: 'Monitor your trips status, carrier locations, and estimated delivery times.' }
      },
      {
        title: { es: 'Carta Porte Automatizada', en: 'Automated Carta Porte' },
        desc: { es: 'Generación digital del complemento de Carta Porte cumpliendo al 100% las normativas de la SCT.', en: 'Digital generation of Carta Porte complement complying 100% with SCT regulations.' }
      },
      {
        title: { es: 'Control de Costos de Fletes', en: 'Freight Cost Audits' },
        desc: { es: 'Audita tarifas, casetas, combustible y viáticos asignados a cada ruta de distribución.', en: 'Audit rates, tolls, fuel, and travel expenses assigned to each distribution route.' }
      }
    ]
  },
  {
    id: 'tms_fleet',
    category: 'logistics',
    tabTitle: { es: 'Estatus Flota', en: 'Fleet Status' },
    title: { es: 'Monitor de Unidades y Operadores', en: 'Units & Operators Monitor' },
    subtitle: { es: 'Estatus operativo y asignación en tiempo real de operadores de transporte y camiones.', en: 'Operational status and real-time assignment of truck drivers and transport units.' },
    imageSrc: '/tms_fleet_status.png',
    badge: { es: 'Monitoreo de Flota', en: 'Fleet Monitoring' },
    highlights: [
      {
        title: { es: 'Estatus de Unidad', en: 'Unit Operational State' },
        desc: { es: 'Indicadores de semáforo para unidades disponibles, en viaje o en mantenimiento.', en: 'Traffic light status indicators for available, on-trip, or maintenance units.' }
      },
      {
        title: { es: 'Asignación de Viaje', en: 'Trip Assignment' },
        desc: { es: 'Vincula de forma directa a cada operador con su unidad y su último folio de viaje activo.', en: 'Directly links each operator with their assigned truck unit and active trip ID.' }
      },
      {
        title: { es: 'Último Reporte GPS', en: 'Last GPS Report' },
        desc: { es: 'Timestamp y geolocalización exacta de la última transmisión de monitoreo satelital.', en: 'Timestamp and exact geolocation of the last satellite monitoring transmission.' }
      }
    ]
  },
  {
    id: 'tms_trips',
    category: 'logistics',
    tabTitle: { es: 'Resumen Viajes', en: 'Trip Summaries' },
    title: { es: 'Dashboard de Indicadores y Resumen de Viajes', en: 'Trip Indicators & Operations Dashboard' },
    subtitle: { es: 'Métricas de productividad de la flota, porcentaje de viajes sin asignar y distribución de fletes.', en: 'Fleet productivity metrics, percentage of unassigned trips, and freight distribution logs.' },
    imageSrc: '/tms_trip_summary.png',
    badge: { es: 'Estadísticas Logísticas', en: 'Logistics Statistics' },
    highlights: [
      {
        title: { es: 'Viajes sin Asignar', en: 'Unassigned Trips Alert' },
        desc: { es: 'Alertas visuales rápidas para despachar rutas que no cuentan con operador asignado.', en: 'Quick visual alerts to dispatch routes that do not have an assigned operator.' }
      },
      {
        title: { es: 'Filtro de Rango de Fechas', en: 'Date Range Selector' },
        desc: { es: 'Filtra las estadísticas operativas por día, semana, mes o rangos de fechas personalizados.', en: 'Filter operational statistics by day, week, month, or custom date ranges.' }
      },
      {
        title: { es: 'Estatus General de la Flota', en: 'Overall Fleet Performance' },
        desc: { es: 'Visualización consolidada de entregas a tiempo, demoras y viajes en tránsito.', en: 'Consolidated visualization of on-time deliveries, delays, and trips in transit.' }
      }
    ]
  },
  {
    id: 'tms_app',
    category: 'logistics',
    tabTitle: { es: 'App Móvil Choferes', en: 'Driver Mobile App' },
    title: { es: 'Aplicación Móvil para Choferes y Operadores', en: 'Mobile App for Drivers & Operators' },
    subtitle: { es: 'Portal móvil simplificado para que los operadores gestionen sus bitácoras de viaje, entrega y evidencias.', en: 'Simplified mobile portal for operators to manage trip logs, delivery status, and signature evidence.' },
    imageSrc: '/tms_aexsa_app.png',
    badge: { es: 'Aplicación Móvil', en: 'Mobile Application' },
    highlights: [
      {
        title: { es: 'Bitácora de Viaje Digital', en: 'Digital Trip Log' },
        desc: { es: 'Registro rápido del inicio, paradas y fin de ruta de forma directa desde el teléfono.', en: 'Quick logging of route starts, stops, and finishes directly from the mobile phone.' }
      },
      {
        title: { es: 'Carga de Evidencia', en: 'Delivery Proof Upload' },
        desc: { es: 'Toma fotos de recibos firmados y capturas de sellos para actualizar el estatus de entrega al instante.', en: 'Capture photos of signed receipts and seals to update delivery status instantly.' }
      },
      {
        title: { es: 'Navegación Integrada', en: 'Integrated Navigation' },
        desc: { es: 'Mapas y direcciones optimizadas de entrega para el conductor basándose en el plan de ruta del TMS.', en: 'Optimized delivery maps and turn-by-turn directions for the driver based on the TMS route plan.' }
      }
    ]
  },
  // ==================== CATEGORY: MANUFACTURING ====================
  {
    id: 'mrp_lote',
    category: 'manufacturing',
    tabTitle: { es: 'Lotes Munters', en: 'Munters Lots' },
    title: { es: 'Control de Lotes y Empaques (Munters)', en: 'Lots & Packaging Control (Munters)' },
    subtitle: { es: 'Dashboard de Resumen Ejecutivo y despacho de cajas de empaque asociadas al Lote L-59644 en la Planta 1 de Apodaca.', en: 'Executive Summary and dispatch dashboard of packaging boxes associated with Lot L-59644 at Planta 1 Apodaca.' },
    imageSrc: '/mrp_lote_dashboard.png',
    badge: { es: 'Control de Lotes', en: 'Production Lots' },
    highlights: [
      {
        title: { es: 'Identificación de Lote L-59644', en: 'Lot L-59644 Tracking' },
        desc: { es: 'Consulta detallada de cajas de embarque (ej. BOX-59644-0057) y estado de empaque por unidad.', en: 'Detailed view of shipping boxes (e.g. BOX-59644-0057) and packaging status per unit.' }
      },
      {
        title: { es: 'Acciones de Manifiesto', en: 'Manifest Dispatch Tools' },
        desc: { es: 'Herramientas rápidas para imprimir el lote completo o exportar manifiestos de despacho.', en: 'Quick action tools to print the complete lot details or export dispatch manifests.' }
      },
      {
        title: { es: 'Integración Planta Apodaca', en: 'Apodaca Plant Integration' },
        desc: { es: 'Vinculación de órdenes de venta (SO) por turno y centro de trabajo físico en planta.', en: 'Linking sales orders (SO) per shift and physical work center on the floor.' }
      }
    ]
  },
  {
    id: 'mrp_stock',
    category: 'manufacturing',
    tabTitle: { es: 'Ubicación Stock', en: 'Stock Locations' },
    title: { es: 'Consulta de Ubicación y Destino de Stock', en: 'Stock Location & Destination Explorer' },
    subtitle: { es: 'Detalle de ubicación física de empaques y resguardo del Lote L-59644 en Almacén.', en: 'Physical location breakdown of packages and storage of Lot L-59644 inside the warehouse.' },
    imageSrc: '/mrp_stock_location.png',
    badge: { es: 'Gestión de Almacenes', en: 'Warehouse Management' },
    highlights: [
      {
        title: { es: 'Ubicación en Bahía de Carga', en: 'Loading Bay Tracking' },
        desc: { es: 'Mapeo detallado de estantes, pasillos y bahías físicas asignadas al empaque (ej. BOX-59644-0075).', en: 'Detailed mapping of shelves, aisles, and physical bays assigned to the package (e.g. BOX-59644-0075).' },
      },
      {
        title: { es: 'Destino Final del Cliente', en: 'Final Customer Destination' },
        desc: { es: 'Dirección física y datos de entrega del cliente final (ej. Polígono Industrial Santa Rosa, Querétaro).', en: 'Physical address and delivery data of the final customer (e.g. Poligono Industrial Santa Rosa, Queretaro).' },
      },
      {
        title: { es: 'Supervisor Responsable', en: 'Reporting Supervisor' },
        desc: { es: 'Nombre del inspector u operador de almacén que reporta y valida el movimiento físico.', en: 'Name of the warehouse inspector or operator who reports and validates the physical movement.' },
      }
    ]
  },
  {
    id: 'mrp_ot',
    category: 'manufacturing',
    tabTitle: { es: 'Orden Trabajo', en: 'Work Orders' },
    title: { es: 'Órdenes de Trabajo en Manufactura', en: 'Manufacturing Work Orders' },
    subtitle: { es: 'Flujo de control de producción y seguimiento de órdenes de trabajo (OT) en planta.', en: 'Production control flow and tracking of Work Orders (WO) on the factory floor.' },
    imageSrc: '/mrp_work_order.png',
    badge: { es: 'Producción', en: 'Manufacturing' },
    highlights: [
      {
        title: { es: 'Estatus del Proceso', en: 'Work Process Status' },
        desc: { es: 'Muestra si la orden está en borrador, proceso o finalizada (ej. OT-000000563 en Proceso).', en: 'Shows if the order is in draft, process, or finished status (e.g. WO-000000563 In Process).' }
      },
      {
        title: { es: 'Consumo de Insumos', en: 'Materials Consumption' },
        desc: { es: 'Listado y validación de materia prima e inventario consumido en la orden de trabajo.', en: 'List and validation of raw materials and inventory consumed inside the work order.' }
      },
      {
        title: { es: 'Horas Hombre y Tiempos', en: 'Man-hours & Lead Times' },
        desc: { es: 'Control de tiempos de operación y operarios asignados al centro de trabajo.', en: 'Operation time tracking and operators assigned to the active work center.' }
      }
    ]
  },
  // ==================== CATEGORY: HR & AI ====================
  {
    id: 'nom035',
    category: 'hr_ai',
    tabTitle: { es: 'Recursos Humanos', en: 'Human Resources' },
    title: { es: 'Cumplimiento NOM-035 con Asistente IA', en: 'NOM-035 Compliance & HR IA Portal' },
    subtitle: { es: 'Portal del empleado con cuestionarios interactivos de clima organizacional y chat de apoyo asistido por IA.', en: 'Employee portal with interactive organizational climate surveys and AI-assisted support chatbot.' },
    imageSrc: '/hrms_nom035.png',
    badge: { es: 'Talento y Clima Laboral', en: 'HR & AI Assistant' },
    highlights: [
      {
        title: { es: 'Cuestionarios NOM-035', en: 'NOM-035 Surveys' },
        desc: { es: 'Aplicación digital y reporte estadístico automático de los cuestionarios I, II y III de factores de riesgo.', en: 'Digital application and automatic statistics report for risk factors surveys I, II, and III.' }
      },
      {
        title: { es: 'Asistente de Salud con IA', en: 'AI Wellness Assistant' },
        desc: { es: 'Chat inteligente disponible 24/7 para canalización de estrés, consultas del buzón de quejas y apoyo.', en: 'Smart chatbot available 24/7 for stress routing, complaint mailbox consultation, and support.' }
      },
      {
        title: { es: 'Expediente Digital de Colaborador', en: 'Digital Employee Folder' },
        desc: { es: 'Resguardo de contratos, evaluaciones de desempeño, capacitaciones y recibos de nómina.', en: 'Secure storage of contracts, performance reviews, training logs, and payslips.' }
      }
    ]
  },
  {
    id: 'nom035_risk',
    category: 'hr_ai',
    tabTitle: { es: 'Factores de Riesgo', en: 'Risk Factors Analysis' },
    title: { es: 'Análisis de Factores de Riesgo Psicosocial', en: 'Psychosocial Risk Factors Analysis' },
    subtitle: { es: 'Visualización de resultados analíticos de la Guía II y III de la NOM-035 para centros de trabajo y departamentos.', en: 'Visualization of analytical results for Guides II and III of NOM-035 across work centers and departments.' },
    imageSrc: '/hrms_risk_factors.png',
    badge: { es: 'Analítica de Recursos Humanos', en: 'HR Analytics' },
    highlights: [
      {
        title: { es: 'Niveles de Riesgo', en: 'Risk Level Breakdown' },
        desc: { es: 'Clasificación de riesgo de factores psicosociales (Nulo, Bajo, Medio, Alto, Muy Alto) según la STPS.', en: 'Risk classification of psychosocial factors (None, Low, Medium, High, Very High) according to STPS guidelines.' }
      },
      {
        title: { es: 'Auditoría Gubernamental', en: 'Government Compliance' },
        desc: { es: 'Generación directa del reporte y evidencias requeridas por inspectores federales del trabajo.', en: 'Direct generation of reports and compliance evidence required by federal labor inspectors.' }
      },
      {
        title: { es: 'Filtrado por Departamento', en: 'Department Comparison' },
        desc: { es: 'Compara y segmenta el clima de trabajo por sucursales físicas para tomar decisiones de bienestar.', en: 'Compare and segment the workplace environment by physical branch to make wellness decisions.' }
      }
    ]
  },
  {
    id: 'cratos',
    category: 'hr_ai',
    tabTitle: { es: 'CRAT OS Auditoría', en: 'CRAT OS AI Audit' },
    title: { es: 'CRAT OS - Planeación Estratégica con IA', en: 'CRAT OS - Strategic Planning with AI' },
    subtitle: { es: 'Cuadro de mando integral (BSC) y auditoría automatizada de proyectos asistida por modelos lingüísticos.', en: 'Balanced Scorecard (BSC) and automated project audits assisted by Large Language Models.' },
    imageSrc: '/cratos_strategic.png',
    badge: { es: 'Estrategia y Auditoría', en: 'Strategic AI Planning' },
    highlights: [
      {
        title: { es: 'Recomendaciones AI Estratégicas', en: 'Strategic AI Recommendations' },
        desc: { es: 'Recibe sugerencias automáticas basadas en IA para resolver desviaciones financieras o cuellos de botella.', en: 'Receive automatic AI-driven suggestions to resolve financial deviations or project bottlenecks.' }
      },
      {
        title: { es: 'Control de Metas Corporativas', en: 'Corporate Goals Control' },
        desc: { es: 'Monitoreo dinámico del avance de OKRs y KPIs de todas las gerencias con semáforos de riesgo.', en: 'Dynamic monitoring of OKRs and KPIs across all managements with visual risk alerts.' }
      },
      {
        title: { es: 'Auditoría de Tareas', en: 'Task Quality Auditing' },
        desc: { es: 'Análisis inteligente del cumplimiento y calidad de entregables en proyectos de desarrollo.', en: 'Intelligent analysis of task completion and deliverable quality in engineering projects.' }
      }
    ]
  },
  {
    id: 'custom_doctorez',
    category: 'hr_ai',
    tabTitle: { es: 'Portal Médico', en: 'Medical Portal' },
    title: { es: 'Portal de Gestión de Consultorios DoctorEZ', en: 'DoctorEZ Clinical Management Portal' },
    subtitle: { es: 'Caso de éxito de desarrollo a la medida: control de expedientes de pacientes, citas y agenda médica.', en: 'Custom development case study: patient clinical files, appointment bookings, and physician schedule.' },
    imageSrc: '/custom_doctorez.png',
    badge: { es: 'Desarrollo a la Medida', en: 'Custom Development' },
    highlights: [
      {
        title: { es: 'Citas del Día y Estatus', en: 'Daily Bookings & Status' },
        desc: { es: 'Listado dinámico de pacientes citados hoy con estatus de consulta (Finalizado, En Espera).', en: 'Dynamic list of patients booked today with consultation status (Finished, Waiting).' }
      },
      {
        title: { es: 'Expediente Clínico Electrónico', en: 'Electronic Health Record (EHR)' },
        desc: { es: 'Acceso seguro al historial clínico del paciente, recetas previas y diagnósticos.', en: 'Secure access to patient clinical history, past prescriptions, and diagnoses.' }
      },
      {
        title: { es: 'Agenda Interactiva', en: 'Interactive Medical Schedule' },
        desc: { es: 'Visualización rápida de horas disponibles por médico para evitar traslapes de citas.', en: 'Quick visualization of available slots per physician to avoid booking overlaps.' }
      }
    ]
  }
];

const InterfaceGallery = () => {
  const { language } = useLanguage();
  const [activeCategory, setActiveCategory] = useState<'sales_finance' | 'logistics' | 'manufacturing' | 'hr_ai'>('sales_finance');
  const [activeTab, setActiveTab] = useState('crm');

  // Filter items by category
  const filteredItems = galleryData.filter(item => item.category === activeCategory);

  // Automatically update the active tab when the category changes
  useEffect(() => {
    if (filteredItems.length > 0) {
      const tabExistsInCategory = filteredItems.some(item => item.id === activeTab);
      if (!tabExistsInCategory) {
        setActiveTab(filteredItems[0].id);
      }
    }
  }, [activeCategory]);

  const activeItem = galleryData.find(item => item.id === activeTab) || filteredItems[0] || galleryData[0];
  const isMobileView = activeItem.id === 'tms_app';

  const getSubTabIcon = (id: string) => {
    switch(id) {
      case 'crm': return <Users className="w-4 h-4" />;
      case 'crm_calendar': return <Calendar className="w-4 h-4" />;
      case 'erp': return <Database className="w-4 h-4" />;
      case 'erp_xml': return <FileText className="w-4 h-4" />;
      case 'kpis_gastos': return <BarChart3 className="w-4 h-4" />;
      case 'tms': return <Truck className="w-4 h-4" />;
      case 'tms_fleet': return <Activity className="w-4 h-4" />;
      case 'tms_trips': return <PieChart className="w-4 h-4" />;
      case 'tms_app': return <Smartphone className="w-4 h-4" />;
      case 'mrp_lote': return <FileSpreadsheet className="w-4 h-4" />;
      case 'mrp_stock': return <MapPin className="w-4 h-4" />;
      case 'mrp_ot': return <ClipboardList className="w-4 h-4" />;
      case 'nom035': return <BrainCircuit className="w-4 h-4" />;
      case 'nom035_risk': return <ShieldAlert className="w-4 h-4" />;
      case 'cratos': return <Search className="w-4 h-4" />;
      case 'custom_doctorez': return <HeartPulse className="w-4 h-4" />;
      default: return <Monitor className="w-4 h-4" />;
    }
  };

  return (
    <section id="interface-gallery" className="py-16 md:py-24 bg-white border-t border-gray-150">
      <div className="container mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold bg-[#115d7b]/10 text-[#115d7b] mb-4 uppercase tracking-wider">
            <Monitor className="w-3.5 h-3.5" />
            {language === 'es' ? 'Nuestras Interfaces' : 'Product Interfaces'}
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4 tracking-tight">
            {language === 'es' ? 'Explora la Plataforma en Acción' : 'Explore the Platform in Action'}
          </h2>
          <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
            {language === 'es' 
              ? 'Conoce el diseño limpio, la potencia analítica y la experiencia intuitiva de nuestras soluciones a través de capturas reales de nuestros sistemas.' 
              : 'Discover the clean design, analytical power, and intuitive user experience of our solutions through real product screenshots.'}
          </p>
        </div>

        <style dangerouslySetInnerHTML={{__html: `
          .scrollbar-none::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-none {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}} />

        {/* Business Area Categories */}
        <div className="flex overflow-x-auto md:flex-wrap justify-start md:justify-center gap-3 mb-6 max-w-5xl mx-auto snap-x snap-mandatory scrollbar-none pb-2 -mx-4 px-4 md:mx-0 md:px-0">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id as any)}
              className={`flex-shrink-0 snap-start px-5 py-3 rounded-2xl text-xs md:text-sm font-bold flex items-center gap-2.5 transition-all duration-300 transform hover:-translate-y-0.5 border ${
                activeCategory === cat.id 
                  ? 'bg-gradient-to-r from-[#115d7b] to-[#0f5269] text-white border-transparent shadow-md shadow-[#115d7b]/15' 
                  : 'bg-white text-gray-700 hover:text-gray-900 hover:bg-gray-50 border-gray-200'
              }`}
            >
              <span className={activeCategory === cat.id ? 'text-white' : 'text-gray-400'}>
                {cat.icon}
              </span>
              <span>{cat.label[language]}</span>
            </button>
          ))}
        </div>

        {/* Dynamic Sub-tabs Selector */}
        <div className="flex overflow-x-auto md:flex-wrap justify-start md:justify-center gap-2 mb-10 max-w-5xl mx-auto p-1.5 bg-gray-50 border border-gray-200 rounded-2xl snap-x snap-mandatory scrollbar-none -mx-4 px-4 md:mx-auto">
          {filteredItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`flex-shrink-0 snap-start px-4 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 transition-all duration-300 ${
                activeTab === item.id 
                  ? 'bg-white text-[#115d7b] shadow-sm border border-gray-200' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/50'
              }`}
            >
              {getSubTabIcon(item.id)}
              <span>{item.tabTitle[language]}</span>
            </button>
          ))}
        </div>

        {/* Dynamic Display Card */}
        <div className="max-w-6xl mx-auto bg-gray-50/50 border border-gray-150 rounded-3xl p-6 md:p-10 shadow-lg">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            
            {/* Left Side: Mock Device or Browser Screenshot */}
            <div className="lg:col-span-7 flex flex-col items-center justify-center">
              {isMobileView ? (
                /* High-Fidelity Smartphone Mockup Frame */
                <div className="relative mx-auto border-[10px] border-slate-900 bg-slate-900 rounded-[40px] shadow-2xl h-[520px] w-[260px] flex-shrink-0 overflow-hidden">
                  {/* Speaker Notch */}
                  <div className="absolute top-0 inset-x-0 h-4 bg-slate-950 flex items-center justify-center z-20">
                    <div className="w-16 h-1 bg-slate-800 rounded-full"></div>
                  </div>
                  {/* Phone Screen Area */}
                  <div className="w-full h-full bg-black pt-4 flex items-center justify-center overflow-hidden">
                    <img
                      src={activeItem.imageSrc}
                      alt={`${activeItem.title[language]} mobile interface`}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-[1.02] cursor-zoom-in"
                      loading="eager"
                    />
                  </div>
                </div>
              ) : (
                /* High-Fidelity Browser Mockup Frame */
                <div className="w-full relative rounded-2xl overflow-hidden border border-gray-250/60 bg-white flex flex-col shadow-xl group">
                  {/* Browser top bar */}
                  <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
                    <div className="flex gap-1.5">
                      <span className="w-3 h-3 rounded-full bg-red-400 block cursor-default"></span>
                      <span className="w-3 h-3 rounded-full bg-yellow-400 block cursor-default"></span>
                      <span className="w-3 h-3 rounded-full bg-green-400 block cursor-default"></span>
                    </div>
                    <div className="flex-1 max-w-xs md:max-w-sm mx-auto bg-white rounded-md text-[9px] md:text-[10px] text-gray-500 text-center py-1 font-mono border border-gray-200">
                      https://laimu.mx/trantor/{activeItem.id}
                    </div>
                    <div className="w-12 flex justify-end">
                      <Maximize2 className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                  </div>
                  {/* Screenshot Body */}
                  <div className="bg-white p-2.5 flex items-center justify-center min-h-[220px] md:min-h-[300px] overflow-hidden relative">
                    <img
                      src={activeItem.imageSrc}
                      alt={`${activeItem.title[language]} system interface`}
                      className="w-full h-auto object-contain rounded-b-xl transition-transform duration-500 group-hover:scale-[1.02] cursor-zoom-in"
                      loading="eager"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Right Side: Description and Highlights Tour */}
            <div className="lg:col-span-5 flex flex-col justify-center">
              {/* Badge */}
              <span className="inline-flex w-fit px-3 py-1 rounded-full text-xs font-extrabold mb-3 tracking-wider uppercase" style={{ backgroundColor: '#e0f2fe', color: '#115d7b' }}>
                {activeItem.badge[language]}
              </span>
              
              {/* Title */}
              <h3 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3 tracking-tight">
                {activeItem.title[language]}
              </h3>
              
              {/* Description */}
              <p className="text-gray-600 mb-8 text-sm md:text-base leading-relaxed">
                {activeItem.subtitle[language]}
              </p>

              {/* Highlights Tour */}
              <div className="space-y-5">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest border-b border-gray-200/60 pb-2">
                  {language === 'es' ? 'Puntos de Interés de la Pantalla' : 'Interface Highlights'}
                </h4>
                {activeItem.highlights.map((highlight, index) => (
                  <div key={index} className="flex gap-3.5 items-start">
                    <span className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 bg-[#115d7b]/10 text-[#115d7b] font-bold text-xs mt-0.5">
                      {index + 1}
                    </span>
                    <div>
                      <h5 className="text-sm font-bold text-gray-900 mb-0.5">
                        {highlight.title[language]}
                      </h5>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        {highlight.desc[language]}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Demo CTA */}
              <div className="mt-8 pt-6 border-t border-gray-200/60">
                <a
                  href="/demo"
                  className="inline-flex items-center gap-2 text-[#115d7b] font-bold text-sm hover:gap-3 transition-all duration-300 group"
                >
                  <span>{language === 'es' ? 'Agendar demostración de esta pantalla' : 'Schedule a live demo of this interface'}</span>
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </a>
              </div>
            </div>

          </div>
        </div>

      </div>
    </section>
  );
};

export default InterfaceGallery;
